import React, { useMemo, useRef, useState } from 'react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { CalendarFormat } from '@fitogram/react-calendar';
import cn from 'classnames';
import { Collapse, SelectChangeEvent } from '@mui/material';
import { endOfDay, startOfDay } from 'date-fns';
import is from 'is_js';

import { BOX_SHADOW } from '~/Constants/CSS.constants';
import { useBoolean } from '~/Hooks';
import { CalendarView } from '~/Models/DTO/Calendar.model';
import { makeStyles } from '~/UIKit/Style/makeStyles';
import { useEventListWrapper } from '../../EventList';
import { CalendarHeader } from '../CalendarHeader';
import { CalendarList } from '../CalendarList';
import { WeeklyView } from '~/Screens/Calendar/CalendarView/WeeklyView';
import { DailyView } from '~/Screens/Calendar/CalendarView/DailyView';
import { PrivateLayout } from '~/Layout/PrivateLayout';
import { WHITE } from '~/Constants/Colors.constants';
import { Filter, FilterGroups } from '~/Components/Filter';
import { useCalendarFilter } from '~/Screens/Calendar/CalendarView/hooks';
import { EVENT_Z_INDEX } from '~/Screens/Calendar/constants';

const useStyles = makeStyles(
  () => ({
    calendarContainer: {
      display: 'grid',
      gridTemplateRows: '60px 1fr',
      boxShadow: BOX_SHADOW,
    },
    collapseList: {
      minWidth: 'unset!important',
    },
    contentContainer: {
      display: 'flex',
      overflowY: 'hidden',
    },
    calendarView: {
      padding: '0 15px',
      width: '100%',
      backgroundColor: WHITE,
      display: 'grid',
    },
    loading: {
      zIndex: EVENT_Z_INDEX + 10,
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%)',
    },
  }),
  { name: 'CalendarDesktopView' },
);

interface ICalendarDesktopView {
  infoSection?: React.ReactNode;
  calendarFormat: CalendarFormat;
}

export const CalendarDesktopView = ({ infoSection, calendarFormat }: ICalendarDesktopView) => {
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const { classes } = useStyles();

  const { state: listOpen, toggle: setToggleList } = useBoolean(true);
  const [view, setView] = useState<CalendarView>(CalendarView.WEEK);
  const {
    open,
    groups,
    onOpen,
    onClose,
    onClear,
    onApply,
    selectedCount,
    appliedFilters,
  } = useCalendarFilter();

  const {
    events,
    goToNextDay,
    calendarController,
    onTodayClick,
    eventsLoading,
    navigateDate,
  } = useEventListWrapper({ calendarFormat, appliedFilters });

  const onSelectView = (e: SelectChangeEvent<CalendarView>) => {
    setView(e.target.value as CalendarView);
  };
  const goToNext = (forward: boolean) => {
    if (view === CalendarView.DAY) {
      goToNextDay(forward ? 1 : -1);
    } else {
      navigateDate(forward);
    }
  };
  const { from, to } = useMemo(() => {
    if (!calendarController.visibleDays || is.empty(calendarController.visibleDays)) {
      return { from: null, to: null };
    }
    return {
      from: startOfDay(calendarController.visibleDays[0]).toISOString(),
      to: endOfDay(
        calendarController.visibleDays[calendarController.visibleDays.length - 1],
      ).toISOString(),
    };
  }, [calendarController.visibleDays]);
  const calendarView = useMemo(
    () =>
      view === CalendarView.WEEK ? (
        <WeeklyView
          selectedDay={calendarController.selectedDay}
          from={from}
          to={to}
          isLoading={eventsLoading}
        />
      ) : (
        <DailyView
          events={events}
          selectedDay={calendarController.selectedDay}
          from={from}
          to={to}
          isLoading={eventsLoading}
        />
      ),
    [view, calendarController.selectedDay, events, from, to, eventsLoading],
  );
  const offsetTop = calendarContainerRef.current?.getBoundingClientRect().y ?? 0;

  return (
    <PrivateLayout backgroundColor={WHITE}>
      {infoSection}
      <div
        ref={calendarContainerRef}
        style={{ maxHeight: `calc(100vh - ${offsetTop + 2}px)` }}
        className={cn(classes.calendarContainer)}
      >
        <CalendarHeader
          view={view}
          toggleList={setToggleList}
          listOpen={listOpen}
          onTodayClick={onTodayClick}
          navigateDate={goToNext}
          selectedDay={calendarController.selectedDay}
          setCalendarView={onSelectView}
          filterComponent={
            <Filter
              open={open}
              onOpen={onOpen}
              onClose={onClose}
              onClear={onClear}
              onApply={onApply}
              selectedCount={selectedCount}
            >
              <FilterGroups groups={groups} />
            </Filter>
          }
        />
        <div className={classes.contentContainer}>
          <Collapse
            in={listOpen}
            orientation="horizontal"
            collapsedSize={0}
            className={classes.collapseList}
          >
            <CalendarList
              events={events[0]}
              onTodayClick={onTodayClick}
              isEventsLoading={eventsLoading}
              calendarController={calendarController}
            />
          </Collapse>
          <div className={classes.calendarView}>
            <DndProvider backend={HTML5Backend}>{calendarView}</DndProvider>
          </div>
        </div>
      </div>
    </PrivateLayout>
  );
};
